import * as React from "react";
import { CalendarIcon } from "lucide-react";
import {
    endOfQuarter,
    format,
    getQuarter,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
} from "date-fns";
import type { DateRange } from "react-day-picker";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";

// // // //

type Preset = {
    label: string;
    /** Ends "today" wherever it's viewed, rather than on a fixed date. */
    openEnded?: boolean;
    getRange: () => DateRange;
};

export type DateRangeChangeMeta = {
    /** True when an open-ended preset (e.g. Year to date) was picked. */
    openEnded: boolean;
};

export function today() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

const quarterLabel = (date: Date) =>
    `Q${getQuarter(date)} ${date.getFullYear()}`;

const DATE_PICKER_PRESETS: Preset[] = [
    {
        label: "Year to date",
        openEnded: true,
        getRange: () => ({ from: startOfYear(today()), to: today() }),
    },
    {
        label: "Quarter to date",
        openEnded: true,
        getRange: () => ({ from: startOfQuarter(today()), to: today() }),
    },
    ...[1, 2, 3, 4].map((n) => {
        const ref = subQuarters(today(), n);
        return {
            label: quarterLabel(ref),
            getRange: () => ({
                from: startOfQuarter(ref),
                to: endOfQuarter(ref),
            }),
        };
    }),
];

function isSameDay(a?: Date, b?: Date) {
    if (!a || !b) return false;
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function rangesEqual(a?: DateRange, b?: DateRange) {
    if (!a || !b) return false;
    return isSameDay(a.from, b.from) && isSameDay(a.to, b.to);
}

function formatRange(range?: DateRange, openEnded = false) {
    if (!range?.from) return "Pick a date range";
    // Open-ended ranges end "today" wherever they're viewed.
    if (openEnded) return `${format(range.from, "LLL d, yyyy")} - Current Date`;
    if (!range.to || isSameDay(range.from, range.to)) {
        return format(range.from, "LLL d, yyyy");
    }
    return `${format(range.from, "LLL d, yyyy")} - ${format(range.to, "LLL d, yyyy")}`;
}

interface DateRangePickerProps {
    value?: DateRange;
    /** Whether `value` is open-ended (ends today). Controlled with `value`. */
    openEnded?: boolean;
    onChange?: (
        range: DateRange | undefined,
        meta?: DateRangeChangeMeta,
    ) => void;
    className?: string;
    align?: "start" | "center" | "end";
}

export function DateRangePicker({
    value,
    openEnded,
    onChange,
    className,
    align = "start",
}: DateRangePickerProps) {
    const [open, setOpen] = React.useState(false);
    // Committed value: what has actually been applied (controlled or uncontrolled).
    const [committedRange, setCommittedRange] = React.useState<
        DateRange | undefined
    >(value);
    // Draft value: edited inside the popover, only committed on Apply.
    const [draftRange, setDraftRange] = React.useState<DateRange | undefined>(
        value,
    );

    // The draft stays open-ended until the user edits it on the calendar.
    const [draftOpenEnded, setDraftOpenEnded] = React.useState(
        openEnded ?? false,
    );

    const committed = value ?? committedRange;
    // Only callers that persist open-ended ranges opt in to the "Current Date" label.
    const committedOpenEnded = openEnded ?? false;

    // Sync the draft with the committed value whenever the popover opens.
    React.useEffect(() => {
        if (open) {
            setDraftRange(committed);
            setDraftOpenEnded(committedOpenEnded);
        }
    }, [open, committed, committedOpenEnded]);

    const commit = React.useCallback(
        (next: DateRange | undefined, meta: DateRangeChangeMeta) => {
            setCommittedRange(next);
            onChange?.(next, meta);
        },
        [onChange],
    );

    // Calendar edits always produce a fixed range.
    const handleDraftChange = React.useCallback(
        (next: DateRange | undefined) => {
            setDraftRange(next);
            setDraftOpenEnded(false);
        },
        [],
    );

    const handleApply = React.useCallback(() => {
        // Hand-picked ranges are fixed; an untouched open-ended draft stays open-ended.
        commit(draftRange, { openEnded: draftOpenEnded });
        setOpen(false);
    }, [commit, draftRange, draftOpenEnded]);

    // Selecting a preset applies it immediately and closes the popover.
    const handlePresetClick = React.useCallback(
        (preset: Preset) => {
            commit(preset.getRange(), {
                openEnded: preset.openEnded ?? false,
            });
            setOpen(false);
        },
        [commit],
    );

    const activePreset = React.useMemo(
        () =>
            DATE_PICKER_PRESETS.find((p) =>
                rangesEqual(p.getRange(), draftRange),
            )?.label,
        [draftRange],
    );

    const label = React.useMemo(
        () => formatRange(committed, committedOpenEnded),
        [committed, committedOpenEnded],
    );

    // Preview of the range currently drafted in the popover, shown next to Apply.
    const draftLabel = React.useMemo(
        () => formatRange(draftRange, draftOpenEnded),
        [draftRange, draftOpenEnded],
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "w-[300px] justify-start text-left font-normal",
                        !committed?.from && "text-muted-foreground",
                        className,
                    )}
                >
                    <CalendarIcon data-icon="inline-start" />
                    <span className="truncate">{label}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align={align}>
                <div className="flex max-sm:flex-col">
                    <div className="flex flex-col gap-1 p-3 max-sm:border-b sm:border-r">
                        {DATE_PICKER_PRESETS.map((preset) => (
                            <Button
                                key={preset.label}
                                variant={
                                    activePreset === preset.label
                                        ? "secondary"
                                        : "ghost"
                                }
                                size="sm"
                                className="justify-start font-normal"
                                onClick={() => handlePresetClick(preset)}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>
                    <div className="flex flex-col">
                        <Calendar
                            mode="range"
                            numberOfMonths={2}
                            defaultMonth={
                                draftRange?.from ?? subMonths(today(), 1)
                            }
                            selected={draftRange}
                            onSelect={handleDraftChange}
                            autoFocus
                        />
                        <div className="flex items-center justify-between gap-2 border-t p-3">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDraftChange(undefined)}
                            >
                                Clear
                            </Button>
                            <div className="flex items-center gap-3">
                                <span className="truncate text-sm text-muted-foreground">
                                    {draftLabel}
                                </span>
                                <Button size="sm" onClick={handleApply}>
                                    Apply
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
